import type { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { type Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SnackbarService } from '../../shared/services/snackbar.service';
import { AuthService } from '../auth/auth.service';
import { getRequestDomainInfo } from '../helpers/request.helper';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private _snackbarService = inject(SnackbarService);
  private _authService = inject(AuthService);
  private _router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((httpResponseError: HttpErrorResponse) => {
        const requestDomainInfo = getRequestDomainInfo(request);
        if (!requestDomainInfo.isAppUrl) {
          return throwError(() => httpResponseError);
        }

        // parse error
        const parsedError = this._parseError(httpResponseError);

        switch (httpResponseError.status) {
          case 401: {
            /** 401 UNAUTHORIZED */
            this._authService.logoutRedirect();
            break;
          }
          case 403: {
            /** 403 FORBIDDEN */
            // display a snackbar when a 403 is encountered.
            this._snackbarService.errorWithHandler(
              httpResponseError.error,
              'Insufficient Access',
              'Some functionality on this page may not work as expected'
            );

            // if 403 error occurs on a resolve and no parent/previous routing has occurred yet
            // send user to app home
            if (!this._router.routerState.snapshot.url) {
              this._sendHome();
            }

            break;
          }

          case 500: {
            // display a snackbar when a 500 error is encountered.
            this._snackbarService.errorWithHandler(httpResponseError.error, 'Error');
            // if 500 error occurs on a resolve and no parent/previous routing has occurred yet
            // send user to app home
            if (!this._router.routerState.snapshot.url) {
              this._sendHome();
            }
            break;
          }
        }

        return throwError(() => parsedError);
      })
    );
  }

  private _parseError(httpResponseError: HttpErrorResponse) {
    // Use Angular error message as default
    const message: string = httpResponseError.error?.message ?? httpResponseError.message;

    return {
      status: httpResponseError.status,
      message,
    };
  }

  private _sendHome() {
    this._router.navigate(['/']);
  }
}
